import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';

const GET_INVESTMENT_ENTITY_DOCUMENT_QUERY = gql(`
  query InvestmentEntityDocument($id: ID!) {
    investmentEntityDocument(id: $id) {
      id
      type
      name
      description
      url
      verifyKyc
    }
  }
`);

type PrefetchedDocument = {
  id?: string | null;
  name?: string | null;
  url?: string | null;
  isDistribution?: boolean;
};

const InvestmentEntityDocumentSlideOver: FC<{
  open: boolean;
  documentId: string;
  onClose: () => void;
  prefetchedDocument?: PrefetchedDocument | null;
  onDocumentOpened?: (document: { id: string; verifyKyc: boolean }) => void;
}> = ({ open = false, documentId, onClose, prefetchedDocument, onDocumentOpened }) => {
  const isDistributionDocument = prefetchedDocument?.isDistribution === true;

  const { loading, error, data, refetch } = useQuery(GET_INVESTMENT_ENTITY_DOCUMENT_QUERY, {
    variables: {
      id: documentId,
    },
    skip: !open || isDistributionDocument,
  });

  function renderContent() {
    if (isDistributionDocument) {
      if (!prefetchedDocument?.url) {
        return (
          <ErrorMessage
            type="alert"
            title="Document unavailable"
            message="This distribution document does not have a file URL."
            showLoginButton={false}
          />
        );
      }

      return (
        <>
          <FormPanelWithReadMode
            loading={false}
            error={undefined}
            onSubmit={() => {}}
            disableEdit={true}
            title="Investment Entity Document"
          >
            <FormInput
              type="text"
              fieldName="name"
              label="Name"
              defaultValue={prefetchedDocument.name ?? 'Distribution Document'}
            />
          </FormPanelWithReadMode>
          <Button onClick={() => window.open(prefetchedDocument.url!, '_blank')}>
            View Document
          </Button>
        </>
      );
    }

    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const investmentEntityDocument = data.investmentEntityDocument;

    return (
      <>
        <FormPanelWithReadMode
          loading={loading}
          error={error}
          onSubmit={() => {}}
          disableEdit={true}
          title="Investment Entity Document"
        >
          <FormInput
            type="text"
            fieldName="name"
            label="Name"
            defaultValue={investmentEntityDocument.name}
          />
        </FormPanelWithReadMode>
        <Button
          onClick={() => {
            if (investmentEntityDocument.url) window.open(investmentEntityDocument.url, '_blank');
            onDocumentOpened?.(investmentEntityDocument);
          }}
          loading={loading}
        >
          View Document
        </Button>
      </>
    );
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Investment Entity Document"
      subTitle="Manage and audit deal document details"
    >
      {renderContent()}
    </SlideOver>
  );
};

export default InvestmentEntityDocumentSlideOver;
